export interface RuntimeVersionPayload {
  version: string
  builtAt?: string
}

export interface UpgradeDetectedPayload {
  currentVersion: string
  latestVersion: string
  reason: string
}

export interface RuntimeVersionCheckerOptions {
  currentVersion: string
  versionUrl: string
  fetchImpl?: typeof fetch
  onUpgradeDetected: (payload: UpgradeDetectedPayload) => void
  logger?: Pick<Console, 'error' | 'warn'>
}

export interface RuntimeVersionChecker {
  checkForUpgrade: (reason: string) => Promise<void>
}

export interface RuntimeVersionDetectorInstallOptions {
  router: {
    onError: (handler: (error: unknown) => void) => void | (() => void)
  }
  checker: RuntimeVersionChecker
  windowTarget?: Window
}

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
  'loading chunk',
  'chunkloaderror',
]

export function isLikelyAssetLoadError(input: unknown): boolean {
  if (input instanceof Event) {
    if (input.type === 'vite:preloadError') return true

    const target = input.target
    if (target instanceof HTMLScriptElement) {
      return target.src.includes('/assets/') || target.src.includes('/public/') || target.type === 'module'
    }
    if (target instanceof HTMLLinkElement) {
      return target.rel === 'stylesheet' && (target.href.includes('/assets/') || target.href.includes('/public/'))
    }
  }

  const message = input instanceof Error
    ? input.message
    : typeof input === 'string'
      ? input
      : ''
  const normalized = message.toLowerCase()
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some(pattern => normalized.includes(pattern))
}

export async function readVersionPayload(input: unknown): Promise<RuntimeVersionPayload> {
  if (!input || typeof input !== 'object') {
    throw new Error('app-version.json must be an object')
  }

  const version = typeof (input as { version?: unknown }).version === 'string'
    ? (input as { version: string }).version.trim()
    : ''
  if (!version) {
    throw new Error('app-version.json version is required')
  }

  const builtAt = typeof (input as { builtAt?: unknown }).builtAt === 'string'
    ? (input as { builtAt: string }).builtAt
    : undefined
  return { version, builtAt }
}

export function createRuntimeVersionChecker(options: RuntimeVersionCheckerOptions): RuntimeVersionChecker {
  const currentVersion = options.currentVersion.trim()
  const logger = options.logger ?? console
  const fetchImpl = options.fetchImpl ?? fetch
  let inFlight: Promise<void> | null = null
  let upgradeAlreadyDetected = false

  if (!currentVersion) {
    logger.warn('[app-version] current VITE_BUILD_VERSION is empty; upgrade detection is disabled')
  }

  const checkForUpgrade = async (reason: string): Promise<void> => {
    if (!currentVersion || upgradeAlreadyDetected) return
    if (inFlight) return inFlight

    inFlight = (async () => {
      try {
        const separator = options.versionUrl.includes('?') ? '&' : '?'
        const response = await fetchImpl(`${options.versionUrl}${separator}ts=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { accept: 'application/json' },
        })
        if (!response.ok) {
          throw new Error(`app-version.json request failed with HTTP ${response.status}`)
        }

        const latest = await readVersionPayload(await response.json())
        if (latest.version === currentVersion) return

        upgradeAlreadyDetected = true
        options.onUpgradeDetected({
          currentVersion,
          latestVersion: latest.version,
          reason,
        })
      } catch (error) {
        logger.error('[app-version] failed to check app version', error)
      } finally {
        inFlight = null
      }
    })()

    return inFlight
  }

  return { checkForUpgrade }
}

export function installRuntimeVersionDetector(options: RuntimeVersionDetectorInstallOptions): () => void {
  const windowTarget = options.windowTarget ?? window

  const handleVitePreloadError = (event: Event) => {
    event.preventDefault()
    void options.checker.checkForUpgrade('vite:preloadError')
  }

  const handleWindowError = (event: Event) => {
    if (isLikelyAssetLoadError(event)) {
      void options.checker.checkForUpgrade('window:error')
    }
  }

  const removeRouterErrorHandler = options.router.onError((error) => {
    if (isLikelyAssetLoadError(error)) {
      void options.checker.checkForUpgrade('router:onError')
    }
  })

  windowTarget.addEventListener('vite:preloadError', handleVitePreloadError)
  windowTarget.addEventListener('error', handleWindowError, true)

  return () => {
    if (typeof removeRouterErrorHandler === 'function') {
      removeRouterErrorHandler()
    }
    windowTarget.removeEventListener('vite:preloadError', handleVitePreloadError)
    windowTarget.removeEventListener('error', handleWindowError, true)
  }
}

export function notifyAppUpgradeDetected(payload: UpgradeDetectedPayload, windowTarget: Window = window): void {
  if (windowTarget.CURRENT_VERSION && typeof windowTarget.CURRENT_VERSION === 'object') {
    windowTarget.CURRENT_VERSION.value = payload.latestVersion
  }

  windowTarget.dispatchEvent(new CustomEvent('carthooks:app-upgrade-detected', { detail: payload }))
}
